<template>
    <banner-alert
        type="warning"
        :icon="icon"
        :closable="false">
        <div>
            <span class="is-bold">{{ title }}</span> {{ message }}
            <learn-more-link
                type="secondary"
                :href="mauSupportLink" />
        </div>
        <template #actions>
            <pendo-button
                :pendo-analytics="`global-banner:${GLOBAL_BANNER_TYPES.OVER_MAU}` + `:${primaryLabel}`"
                :data-cy="`${primaryLabel}:button`"
                :label="primaryLabel"
                size="mini"
                type="primary"
                @click="$emit('primaryAction')" />
            <pendo-button
                v-if="isEligibleForTrial"
                :pendo-analytics="`global-banner:${GLOBAL_BANNER_TYPES.OVER_MAU}:Start trial`"
                data-cy="Start trial:button"
                label="Start trial"
                size="mini"
                type="subtle"
                @click="$emit('startTrial')" />
        </template>
    </banner-alert>
</template>

<script>
import { PendoButton } from '@pendo/components';
import BannerAlert from '@/common/banner-notification/banner-alert/banner-alert.vue';
import GLOBAL_BANNER_TYPES from '@/constants/global-banner-types';
import { MAU_LIMIT_SUPPORT_ARTICLE_LINK } from '@/free/free.constants.js';
import LearnMoreLink from '@/common/learn-more-link/learn-more-link.vue';
import { getPrimaryLabel } from './global-banner-types.utils';

export default {
    name: 'OverMauBanner',
    components: {
        BannerAlert,
        PendoButton,
        LearnMoreLink,
    },
    props: {
        mauLimit: {
            type: Number,
            required: true,
        },
        hardPaywallEnabled: {
            type: Boolean,
            required: true,
        },
        plan: {
            type: String,
            required: true,
        },
        isEligibleForTrial: {
            type: Boolean,
            required: true,
        },
        canAccessStarterUpgrade: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['primaryAction', 'startTrial'],
    data () {
        return {
            mauSupportLink: MAU_LIMIT_SUPPORT_ARTICLE_LINK,
            GLOBAL_BANNER_TYPES,
            icon: {
                type: 'slash',
                stroke: 'var(--palette-yellow-80)',
            },
        };
    },
    computed: {
        message () {
            if (!this.hardPaywallEnabled) {
                return 'To avoid any interruptions in your experience, upgrade your plan today.';
            }
            if (this.plan === 'pendoFree') {
                return `You've exceeded the ${this.mauLimit.toLocaleString(
                    'en',
                )} monthly active user (MAU) limit of your current Pendo plan. To continue creating new guides and working with your full data set, upgrade your plan today.`;
            }

            return `You've exceeded the ${this.mauLimit.toLocaleString(
                'en',
            )} monthly active user (MAU) limit of your current Pendo plan. Reports and dashboards now reflect a limited view of your data. To continue growing with Pendo, upgrade your plan today.`;
        },
        title () {
            if (!this.hardPaywallEnabled) {
                return `You've exceeded the ${this.mauLimit.toLocaleString(
                    'en',
                )} monthly active user (MAU) limit of your current Pendo plan.`;
            }
            if (this.plan === 'pendoFree') {
                return 'Your access to guides and data analytics is now limited.';
            }

            return 'Your access to data analytics is now limited.';
        },
        primaryLabel () {
            return getPrimaryLabel(this.plan, this.canAccessStarterUpgrade);
        },
    },
};
</script>

<style lang="less" scoped>
.pendo-banner {
    .pendo-alert {
        border: none;
        border-radius: 0;
    }
    .is-bold {
        font-weight: bold;
    }
}
</style>
